import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Slide,
  DialogTitle,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DownloadIcon } from "../../../../shared/assets/icons";
import PixellPdiPageOne from "./PixellPdiPageOne";
import axios from "axios";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AnswerPreviewModal = ({ template, onClose }) => {
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isPdfStylePreviewOpen, setIsPdfStylePreviewOpen] = useState(false);
  const [categoryBasedQuestions, setCategoryBasedQuestions] = useState({});
  const [campaignDetails, setCampaignDetails] = useState({});
  const [jobData, setJobData] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  const serverURL = process.env.REACT_APP_URL;

  useEffect(() => {
    const fetchAnswers = async () => {
      if (!template?.productType || !template?.campaignId) return;

      setLoading(true);

      try {
        const res = await fetch(
          `${serverURL}/PDI/fetchQuestionnairesWithAnswersLinkedWithCampaign?productType=${encodeURIComponent(
            template.productType
          )}&campaignId=${encodeURIComponent(template.campaignId)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const data = await res.json();
        setAnswers(data.data || []);
      } catch (error) {
        console.error("Fetch error:", error);
        setAnswers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnswers();
  }, [template]);

  const pdfUrl =
    "https://pixelldasboardbucket.s3.ap-south-1.amazonaws.com/PDI+REport+Final.pdf";

  const handleDownload = async () => {
    const response = await fetch(pdfUrl);
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "PDI Report.pdf";
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const pdiPdfPreview = async (campaignId, productType) => {
    try {
      setPdfLoading(true);

      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${serverURL}/PDI/fetchPDIAnswersForPDIReport`,
        {
          params: { campaignId, productType },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCategoryBasedQuestions(res.data.questionAnswer || {});
      setJobData(Array.isArray(res.data.data) ? res.data.data : []);

      setCampaignDetails(res.data.campaignDetails || {});
      setIsPdfStylePreviewOpen(true);
    } catch (error) {
      console.error("Error fetching PDI PDF preview data:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      {/* Main Modal Box */}
      <div className="bg-white rounded-2xl p-6 w-[700px] max-h-[80vh] overflow-y-auto shadow-xl relative">
        {/* Header */}
        <div className="flex flex-row-reverse items-center mb-4 justify-between">
          <div className="flex gap-2 flex-row-reverse">
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              &times;
            </button>
            {template.campaignId == "68d773c18b82e35760cd2241" && (
              <Tooltip title="Download PDI Report" placement="left">
                <IconButton onClick={handleDownload}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Preview PDI Report" placement="left">
              <IconButton
                onClick={() =>
                  pdiPdfPreview(template.campaignId, template.productType)
                }
              >
                <span className="text-blue-600 font-semibold text-sm">
                  Preview
                </span>
              </IconButton>
            </Tooltip>
          </div>

          <h2 className="text-xl font-bold text-[#418cff]">
            {template.productType} — Campaign Answers
          </h2>
        </div>

        {/* Answers */}
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <CircularProgress />
          </div>
        ) : answers.length === 0 ? (
          <p className="text-gray-500 text-center mt-10">No answers found.</p>
        ) : (
          answers.map((job) => (
            <div
              key={job._id}
              className="border border-gray-300 rounded-lg p-4 mb-5 bg-gray-50"
            >
              <h3 className="text-lg font-semibold text-blue-600 mb-2">
                Store Name — {job?.jobId?.storeName}
              </h3>

              {job.questions?.length > 0 ? (
                job.questions.map((q, i) => (
                  <div
                    key={i}
                    className="border border-gray-200 rounded-lg p-3 mb-3 bg-white"
                  >
                    <p className="font-semibold text-gray-800">
                      {i + 1}. {q.question}
                    </p>

                    <p className="text-sm text-gray-600 mt-1">
                      <b>Category:</b> {q.category || "N/A"} |
                      <b> Subcategory:</b> {q.subCategory || "N/A"}
                    </p>

                    <p className="text-sm text-gray-600 mt-1">
                      <b>Type:</b> {q.questionType || "N/A"} |<b> Mandatory:</b>{" "}
                      {q.mandatory === "Yes" ? "Yes" : "No"}
                    </p>

                    <span className="font-semibold text-sm text-gray-700">
                      Image:
                    </span>

                    {q.imageURL ? (
                      <img
                        src={q.imageURL}
                        alt="preview"
                        className="w-28 h-20 object-cover rounded-md shadow-sm border cursor-pointer"
                        onClick={() => {
                          setSelectedImage(q.imageURL);
                          setIsImageModalOpen(true);
                        }}
                      />
                    ) : (
                      <span className="text-gray-500 text-sm">No image</span>
                    )}

                    <p className="mt-2 text-gray-800">
                      <b>Answer:</b> {q.answer || "No answer provided"}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic ml-2">No questions found.</p>
              )}
            </div>
          ))
        )}
      </div>

      {/* Image Viewer */}
      {isImageModalOpen && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[999]">
          <button
            onClick={() => setIsImageModalOpen(false)}
            className="absolute top-10 right-10 bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-90"
          >
            <CloseIcon />
          </button>

          <img
            src={selectedImage}
            alt="zoom"
            className="max-w-[90%] max-h-[90%] object-contain rounded-lg border shadow-lg"
          />
        </div>
      )}

      {/* PDF Styled Preview Dialog */}
      <Dialog
        open={isPdfStylePreviewOpen}
        onClose={() => setIsPdfStylePreviewOpen(false)}
        maxWidth="lg"
        TransitionComponent={Transition}
        PaperProps={{
          style: {
            background: "white",
            borderRadius: 12,
            height: "90vh",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            border: 0,
          }}
        >
          {/* Close Button */}
          <div className="flex justify-between items-center ">
            <div></div>
            <div></div>

            <div>
              <IconButton
                onClick={() => setIsPdfStylePreviewOpen(false)}
                sx={{
                  background: "rgba(0,0,0,0.6)",
                  color: "white",
                  "&:hover": { background: "rgba(0,0,0,0.8)" },
                  zIndex: 3,
                  alignContent: "right",
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            padding: 0,
            background: "#fff",
            overflowY: "auto",
            overflowX: "hidden",
            position: "relative",
            minHeight: "300px", // ensures loader stays centered
          }}
        >
          {/* -------- PDF LOADING SPINNER -------- */}
          {pdfLoading ? (
            <div
              style={{
                width: "100%",
                height: "70vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 3,
              }}
            >
              <CircularProgress size={60} />
            </div>
          ) : (
            <PixellPdiPageOne
              campaignDetails={campaignDetails}
              // categoryBasedQuestions={categoryBasedQuestions}
              jobData={jobData}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnswerPreviewModal;
